import { makeAutoObservable, runInAction } from 'mobx'
import { openApi } from '../../helpers/request'
import { FormEvent } from 'react'
import { notification } from 'antd'
import i from '../../translations/i'
import { storeLocal } from '../../helpers/stores'

export interface AppInterface {
    id: number
    key: string
    enabled: boolean
    installed: boolean
    config: any
    name_class: string
    category: string
    min_rate: number
    icon: string
}

// Разделы витрины. Категория приложения на бэкенде (apps.category) → раздел; пустая — «скоро»
export type AppGroup = 'sync' | 'crm' | 'payment' | 'delivery' | 'bonus' | 'auth' | 'emails' | 'company' | 'search' | 'other' | 'soon'

export const APP_GROUPS: AppGroup[] = ['sync', 'crm', 'payment', 'delivery', 'bonus', 'auth', 'emails', 'company', 'search', 'other', 'soon']

const CATEGORY_GROUP: Record<string, AppGroup> = {
    sync: 'sync',
    crm: 'crm',
    payment: 'payment',
    delivery: 'delivery',
    bonus: 'bonus',
    authSms: 'auth',
    authExternal: 'auth',
    auth_external_sms: 'auth',
    transactional_emails: 'emails',
    company_data: 'company',
    search: 'search',
}

export const appGroup = (app: AppInterface): AppGroup =>
    !app.category ? 'soon' : CATEGORY_GROUP[app.category] ?? 'other'

// Приложение недоступно на текущем тарифе
export const appLocked = (app: AppInterface) => Number(storeLocal.get('pricing_plan')) < Number(app.min_rate ?? 0)

export const appName = (app: any): string => app?.config?.local?.name || app?.config?.name || app?.key || ''

export const appEvents = (app: any): string[] =>
    `${app?.config?.events ?? ''}`.split(',').map((event: string) => event.trim()).filter(Boolean)

class __apps {

    list: AppInterface[] = []
    installed: AppInterface[] = []
    loading = false

    search = ''
    group: AppGroup | 'all' = 'all'

    // Открытое в боковой панели приложение (GET store/{id})
    data: any = null
    drawerOpen = false
    detailLoading = false
    saving = false

    constructor() {
        makeAutoObservable(this)
    }

    get all(): AppInterface[] {
        return [...this.installed, ...this.list]
    }

    // Сколько приложений в каждом разделе — для чипов фильтра
    get groupCounts(): Record<string, number> {
        const counts: Record<string, number> = {}
        this.all.forEach(app => {
            const group = appGroup(app)
            counts[group] = (counts[group] ?? 0) + 1
        })
        return counts
    }

    private matches(app: AppInterface) {
        if (this.group !== 'all' && appGroup(app) !== this.group) return false

        const query = this.search.trim().toLowerCase()
        if (!query) return true

        const local = app.config?.local ?? {}
        return [appName(app), local.annotation, local.description, app.key]
            .some(text => `${text ?? ''}`.toLowerCase().includes(query))
    }

    get installedFiltered(): AppInterface[] {
        return this.installed.filter(app => this.matches(app))
    }

    // Каталог по разделам в порядке APP_GROUPS; подключённые — отдельно сверху
    get sections(): { group: AppGroup, apps: AppInterface[] }[] {
        const apps = this.list.filter(app => this.matches(app))

        return APP_GROUPS
            .map(group => ({ group, apps: apps.filter(app => appGroup(app) === group) }))
            .filter(section => section.apps.length > 0)
    }

    get filtered(): boolean {
        return !!this.search.trim() || this.group !== 'all'
    }

    setSearch(value: string) {
        this.search = value
    }

    setGroup(group: AppGroup | 'all') {
        this.group = group
    }

    resetFilters() {
        this.search = ''
        this.group = 'all'
    }

    getList() {
        this.loading = true

        openApi('get', '/store')
            .then(result => runInAction(() => {
                this.list = result.data.other ?? []
                this.installed = result.data.installed ?? []
            }))
            .catch(err => console.log(err?.response))
            .finally(() => runInAction(() => this.loading = false))
    }

    // ---------- Боковая панель ----------

    open(id: number) {
        if (this.data?.id !== id) this.data = null
        this.drawerOpen = true
        this.getOne(id)
        window.history.replaceState(null, '', `/apps/${id}`)
    }

    close() {
        this.drawerOpen = false
        window.history.replaceState(null, '', '/apps')
        this.getList()
    }

    // После закрытия анимации панели
    clear() {
        if (!this.drawerOpen) this.data = null
    }

    getOne(id: number) {
        this.detailLoading = true

        return openApi('get', `/store/${id}`)
            .then(result => runInAction(() => {
                this.data = result.data
                if (!Array.isArray(this.data.fields)) this.data.fields = []
            }))
            .catch(error => {
                console.log(error?.response)
                runInAction(() => this.drawerOpen = false)
            })
            .finally(() => runInAction(() => this.detailLoading = false))
    }

    // Поля настроек в порядке, заданном приложением; служебный eventsDisabled — не поле
    get settings(): any[] {
        if (!this.data?.data) return []
        return Object.keys(this.data.data)
            .filter(key => key !== 'eventsDisabled')
            .map(key => this.data.data[key])
            .filter((field: any) => field && field.type)
    }

    // Поля, которые появятся после первого сохранения: их варианты подгружаются из подключённой системы
    get pendingSettings(): number {
        return this.data && !this.data.installed ? this.settings.filter((field: any) => field.then).length : 0
    }

    settingLabel(field: any): string {
        return this.data?.config?.local?.settings?.[field.key] ?? field.label ?? field.key
    }

    setSetting(field: any, value: any) {
        field.value = value
    }

    setEnabled(enabled: boolean) {
        if (this.data) this.data.enabled = enabled
    }

    eventEnabled(event: string): boolean {
        const disabled: string[] = this.data?.data?.eventsDisabled ?? []
        return !disabled.includes(event)
    }

    setEvent(event: string, enabled: boolean) {
        const disabled: string[] = (this.data.data.eventsDisabled ?? []).filter((el: string) => el !== event)
        if (!enabled) disabled.push(event)
        this.data.data.eventsDisabled = disabled
    }

    addCustomField() {
        this.data.fields.push({
            field_key: null,
            comment: null,
            entity_type: null,
            entity_id: null,
            data: { value: '' },
        })
    }

    setCustomField(field: any, key: string, value: any) {
        if (key === 'value') field.data = { ...(field.data ?? {}), value }
        else field[key] = value
    }

    deleteField(index: number) {
        this.data.fields.splice(index, 1)
    }

    saveApplication() {
        const data = new FormData()
        if (this.data.enabled) data.append('enabled', '1')
        data.append('data', JSON.stringify(this.data.data))

        this.data.fields.forEach((field: any, index: number) => {
            if (field.id && field.id > 0) data.append(`fields[${index}][id]`, `${field.id}`)
            if (field.comment) data.append(`fields[${index}][comment]`, field.comment)
            if (field.field_key) data.append(`fields[${index}][field_key]`, field.field_key)
            if (field.entity_type) data.append(`fields[${index}][entity_type]`, field.entity_type)
            if (field.entity_id) data.append(`fields[${index}][entity_id]`, field.entity_id)
            if (field.data?.value && typeof field.data.value === 'string') data.append(`fields[${index}][data][value]`, field.data.value)
            data.append('is_data', '1')
        })

        const wasInstalled = this.data.installed
        this.saving = true

        openApi('post', `/store/${this.data.id}`, data)
            .then(result => {
                this.getOne(result.data.app_id ?? this.data.id)
                notification.success({
                    message: i.t('def:success.success'),
                    description: wasInstalled ? i.t('def:success.edit') : i.t('appsStore:connected'),
                })
            })
            .catch(error => {
                console.log(error?.response)
                notification.error({
                    message: i.t('def:errors.error'),
                    description: error?.response?.data?.message ?? i.t('def:errors.somethingWentWrong'),
                })
            })
            .finally(() => runInAction(() => this.saving = false))
    }

    save(e: FormEvent) {
        e.preventDefault()
        this.saveApplication()
    }

    delete(id: number) {
        return openApi('delete', `/store/${id}`)
            .then(() => {
                notification.success({
                    message: i.t('def:success.success'),
                    description: i.t('appsStore:disconnected'),
                })
                runInAction(() => this.drawerOpen = false)
                window.history.replaceState(null, '', '/apps')
                this.getList()
            })
            .catch(error => {
                notification.error({
                    message: i.t('def:errors.error'),
                    description: error?.response?.data?.message ?? i.t('def:errors.somethingWentWrong'),
                })
            })
    }
}

export default new __apps()
